/**
 * HannoNexus — Central data model.
 * All ecosystem structure, sub-products, projects, impact metrics, roadmap.
 * Source of truth for HomePage sections and pillar pages.
 */

import {
  Zap, Car, Cloud, Building2, GraduationCap,
  Sun, Cpu, Activity, Plug, Wrench, BookOpen,
  Battery, Factory, Truck, Rss, Waves, Lightbulb, Boxes,
  Database, Network, ShoppingBag,
  Brain, Server, Code2, Radio,
  Leaf, Users, TrendingUp, Globe2,
  Wine, GlassWater, Coffee,
} from "lucide-react";

// The 5 core HX ecosystems
export const ECOSYSTEMS = [
  {
    slug: "hx-energy",
    icon: Zap,
    emoji: "⚡",
    color: "#0A9B5F",
    image: "/hanno/hx-energy.png",
    name: "HX HannoNexus Energy",
    short: "HX Energy",
    tagline: "Ecossistema de energia inteligente",
    description: "Energia inteligente para produção, armazenamento e distribuição. Do sol à rede, do painel ao ponto de carregamento — uma cadeia de valor totalmente integrada.",
    subs: [
      { code: "HX Solar",        icon: Sun,     desc: "Produção fotovoltaica residencial, comercial e utility-scale." },
      { code: "HX Core",         icon: Cpu,     desc: "Sistemas de armazenamento (BESS) residencial, comercial e industrial." },
      { code: "HX Pulse",        icon: Activity,desc: "Plataforma digital de monitorização, IA e telemetria em tempo real." },
      { code: "HX Charge",       icon: Plug,    desc: "Rede de carregamento AC/DC para EV — cafés, hotéis, corredores." },
      { code: "HX Install Network", icon: Wrench,desc: "Rede certificada de parceiros de instalação e manutenção." },
      { code: "HX Academy",      icon: BookOpen,desc: "Formação e certificação técnica em energia solar e armazenamento." },
    ],
  },
  {
    slug: "hx-automotive",
    icon: Car,
    emoji: "🚗",
    color: "#0A9B5F",
    image: "/hanno/hx-automotive.png",
    name: "HX HannoNexus Automotive",
    short: "HX Automotive",
    tagline: "Ecossistema de mobilidade elétrica",
    description: "Mobilidade elétrica completa — do citadino compacto ao grand tourer, das bicicletas OBOWAY às motas elétricas. Marca própria private-label desenvolvida para os mercados lusófonos, com montagem local e assistência técnica autorizada.",
    // Private-label vehicle lineup — HannoNexus branded EVs + OBOWAY two-wheelers
    vehicles: [
      { name: "HX Nudi",         segment: "Compact Urban EV",     image: "/hanno/vehicles/hx-nudi.png",         desc: "Citadino compacto elétrico — mobilidade urbana diária, ideal para cidades e ilhas." },
      { name: "HX Zuri",         segment: "Executive Sedan",       image: "/hanno/vehicles/hx-zuri.png",         desc: "Sedan executivo elétrico com autonomia estendida — corporate & premium mobility." },
      { name: "HX Pirys",        segment: "Adventure SUV",         image: "/hanno/vehicles/hx-pirys.png",        desc: "SUV elétrico off-road-ready para terrenos vulcânicos e estradas costeiras." },
      { name: "HX Mionga",       segment: "Utility Pickup",        image: "/hanno/vehicles/hx-mionga.png",       desc: "Pickup elétrica utilitária — agricultura, obras públicas e frotas de serviço." },
      { name: "HX Mionga Cargo", segment: "Commercial Van",        image: "/hanno/vehicles/hx-mionga-cargo.png", desc: "Van elétrica de carga para logística de última milha e distribuição comercial." },
      { name: "HX Atlan",        segment: "Flagship Grand Tourer", image: "/hanno/vehicles/hx-atlan.png",        desc: "Grand tourer elétrico topo de gama — o carro de assinatura HannoNexus." },
      { name: "OBOWAY E-Bike",   segment: "Electric Bicycle · OBOWAY",   image: "/hanno/vehicles/oboway-e-bike.png", desc: "Bicicleta elétrica OBOWAY — mobilidade leve urbana e turística, ideal para ilhas e cidades históricas." },
      { name: "OBOWAY Moto",     segment: "Electric Motorcycle · OBOWAY", image: "/hanno/vehicles/oboway-moto.png",   desc: "Mota elétrica OBOWAY — solução ágil de mobilidade urbana e last-mile delivery com zero emissões." },
    ],
    subs: [
      { code: "HX EV",       icon: Car,     desc: "Portfólio private-label de veículos elétricos: Nudi, Zuri, Pirys, Mionga, Mionga Cargo, Atlan." },
      { code: "OBOWAY",      icon: Rss,     desc: "Marca própria de mobilidade leve elétrica: e-bikes e motas 100% elétricas." },
      { code: "HX Assembly", icon: Factory, desc: "Centros de montagem local com transferência tecnológica OEM." },
      { code: "HX Battery",  icon: Battery, desc: "Baterias de tração, second-life e sistemas de armazenamento veicular." },
      { code: "HX Mobility", icon: Rss,     desc: "Plataforma de mobilidade partilhada e serviços urbanos." },
      { code: "HX Fleet",    icon: Truck,   desc: "Gestão inteligente de frotas com telemetria e otimização de rotas." },
      { code: "HX Service",  icon: Wrench,  desc: "Rede de assistência técnica autorizada e peças originais." },
    ],
  },
  {
    slug: "hx-systems",
    icon: Cloud,
    emoji: "💻",
    color: "#0A9B5F",
    image: "/hanno/hx-systems.png",
    name: "HX Systems",
    short: "HX Systems",
    tagline: "Plataformas digitais & infraestrutura tecnológica",
    description: "A camada digital que suporta toda a operação HannoNexus — cloud soberana, inteligência artificial, dados e IoT industrial.",
    subs: [
      { code: "HX Cloud", icon: Server,   desc: "Cloud pública, híbrida e edge — infraestrutura escalável e soberana." },
      { code: "HX AI",    icon: Brain,    desc: "Modelos de IA, LLMs privados, visão computacional e otimização preditiva." },
      { code: "HX HUB",   icon: Network,  desc: "Centro digital unificado — dashboards, workflows e integrações empresariais." },
      { code: "HX Data",  icon: Database, desc: "Data lakes, warehouses e pipelines de analytics em tempo real." },
      { code: "HX IoT",   icon: Radio,    desc: "Plataforma IoT industrial — sensores, telemetria e gestão de dispositivos." },
    ],
  },
  {
    slug: "hx-infrastructure",
    icon: Building2,
    emoji: "🏗",
    color: "#0A9B5F",
    image: "/hanno/hx-infrastructure.png",
    name: "HX Infrastructure",
    short: "HX Infrastructure",
    tagline: "Infraestruturas inteligentes",
    description: "Construímos a infraestrutura física das cidades do futuro — redes elétricas, iluminação pública, água e armazenamento distribuído.",
    subs: [
      { code: "Smart Cities",     icon: Building2,  desc: "Sistemas integrados de gestão urbana — mobilidade, energia, segurança." },
      { code: "Microgrids",       icon: Boxes,      desc: "Redes elétricas autónomas para comunidades e zonas industriais." },
      { code: "Water Systems",    icon: Waves,      desc: "Gestão inteligente de água — dessalinização, tratamento, distribuição." },
      { code: "Public Lighting",  icon: Lightbulb,  desc: "Iluminação pública LED com IoT — telemetria, dimming, sensores." },
      { code: "Energy Storage",   icon: Battery,    desc: "Armazenamento distribuído para estabilidade e resiliência da rede." },
    ],
  },
  {
    slug: "hx-academy",
    icon: GraduationCap,
    emoji: "🎓",
    color: "#0A9B5F",
    image: "/hanno/hx-academy.png",
    name: "HX Academy",
    short: "HX Academy",
    tagline: "Formação & certificação",
    description: "Formamos e certificamos os profissionais que operam o ecossistema HannoNexus — engenharia, energia, mobilidade, software e IA.",
    subs: [
      { code: "Energy",       icon: Zap,           desc: "Instalação e operação de sistemas solares, BESS e redes inteligentes." },
      { code: "Mobility",     icon: Car,           desc: "Manutenção EV, gestão de frota e infraestrutura de carregamento." },
      { code: "Software",     icon: Code2,         desc: "Engenharia de software, cloud e desenvolvimento de plataformas." },
      { code: "AI",           icon: Brain,         desc: "Machine learning aplicado, visão computacional e IA operacional." },
      { code: "Engineering",  icon: Wrench,        desc: "Engenharia elétrica, mecânica e sistemas industriais." },
    ],
  },
  {
    slug: "mionga",
    icon: Wine,
    emoji: "🍾",
    color: "#D4A857",
    image: "/mionga/cafe-interior-day.jpg",
    name: "MIONGA Beverages",
    short: "MIONGA",
    tagline: "Ecossistema premium de lifestyle",
    description: "Bebidas de assinatura, café artesanal Moin | Olá, formatos flagship e alianças estratégicas entre a Europa (Uetze) e a CPLP — a marca de estilo de vida do grupo HannoNexus.",
    strategic: true,
    subs: [
      { code: "MIONGA Water",     icon: GlassWater, desc: "Água mineral natural · pH 7,2 · Rica em potássio." },
      { code: "MIONGA Sparkling", icon: GlassWater, desc: "Água mineral com gás · Refrescante e equilibrada." },
      { code: "MIONGA Juice",     icon: Leaf,       desc: "Sumos 100% naturais · Sem conservantes." },
      { code: "MIONGA Iced Tea",  icon: Leaf,       desc: "Chá gelado premium · Ingredientes selecionados." },
      { code: "MIONGA Coffee",    icon: Coffee,     desc: "Blend premium · 100% Arábica." },
      { code: "Moin | Olá Cafés", icon: Coffee,     desc: "Formato flagship urbano com Kinderecke, bar e retail." },
    ],
  },
];

// Impact stats
export const IMPACT_STATS = [
  { value: "20 000+",  label: "Sistemas instalados",   icon: Boxes },
  { value: "60 MW",    label: "Energia limpa",         icon: Sun },
  { value: "500+",     label: "Profissionais certificados", icon: Users },
  { value: "300+",     label: "Empregos qualificados", icon: TrendingUp },
  { value: "15 000 t", label: "CO₂ evitado",           icon: Leaf },
];

// Strategic projects
export const STRATEGIC_PROJECTS = [
  {
    slug: "cabo-verde",
    flag: "🇨🇻",
    country: "Cabo Verde",
    focus: "HX Energy",
    image: "/hanno/project-cabo-verde.png",
    tagline: "Independência energética arquipelágica.",
    highlights: [
      "Energia Solar",
      "BESS (Battery Energy Storage)",
      "Smart Grid inter-ilhas",
      "Centros Logísticos",
      "HX Academy",
    ],
    description: "Implementação de um ecossistema energético completo — produção solar, armazenamento, distribuição inteligente e formação técnica local.",
  },
  {
    slug: "sao-tome",
    flag: "🇸🇹",
    country: "São Tomé e Príncipe",
    focus: "HX Automotive",
    image: "/hanno/project-sao-tome.png",
    tagline: "Mobilidade elétrica insular.",
    highlights: [
      "Veículos Elétricos",
      "Centro de Montagem",
      "Rede de Carregamento",
      "Plataforma Digital",
      "Formação Técnica",
    ],
    description: "Ecossistema de mobilidade elétrica com montagem local, rede de carregamento, gestão digital de frota e formação técnica.",
  },
  {
    slug: "mionga",
    flag: "🇩🇪🇨🇻",
    country: "Uetze → Cabo Verde",
    focus: "MIONGA Beverages",
    image: "/mionga/cafe-interior-day.jpg",
    tagline: "Pureza que viaja. Qualidade que fica.",
    highlights: [
      "MIONGA Water · Sparkling · Juice · Iced Tea · Coffee",
      "Moin | Olá Coffee Connection · Uetze HQ",
      "Rota Atlântica ~3.900 km",
      "Frota Cargo Elétrica · Zero Emissões",
      "Rede Hoteleira & F&B Premium",
    ],
    description: "Ecossistema premium de lifestyle e hospitalidade — bebidas oficiais, café de assinatura, formatos flagship e alianças estratégicas entre a Europa e a CPLP.",
    link: "/mionga",
    linkLabel: "Explorar MIONGA",
  },
];

// How it works — operational flow
export const HOW_IT_WORKS = [
  { step: "Produção de Energia",         desc: "Solar · Eólico · Cogeração" },
  { step: "Armazenamento Inteligente",   desc: "BESS residencial · comercial · industrial" },
  { step: "Gestão Digital",              desc: "HX Cloud · HX Data · HX HUB" },
  { step: "Infraestrutura",              desc: "Smart Grid · Microgrids · Public Lighting" },
  { step: "Mobilidade Elétrica",         desc: "EV · Fleet · Charging" },
  { step: "Monitorização IA",            desc: "HX Pulse · HX AI · Manutenção Preditiva" },
  { step: "Análise em Tempo Real",       desc: "Dashboards executivos · KPIs · Alertas" },
];

// Roadmap 2026-2030
export const ROADMAP = [
  { year: "2026", milestone: "Planeamento Estratégico",           desc: "Modelação financeira, parcerias, licenças, engenharia preliminar." },
  { year: "2027", milestone: "Centro Logístico & HX Academy",     desc: "Abertura do primeiro centro logístico e campus de formação." },
  { year: "2028", milestone: "Operação HX Energy",                desc: "Comissionamento de solar, BESS e smart grid em Cabo Verde." },
  { year: "2029", milestone: "Operação HX Automotive",            desc: "Início da montagem local de EV e rede de carregamento em STP." },
  { year: "2030", milestone: "Expansão Regional",                 desc: "Angola, Moçambique, Guiné-Bissau — replicação do modelo integrado." },
];

// HX Pulse capabilities
export const HXPULSE_FEATURES = [
  { icon: Brain,    label: "Inteligência Artificial" },
  { icon: Activity, label: "Dashboards Executivos" },
  { icon: Radio,    label: "Telemetria Industrial" },
  { icon: Wrench,   label: "Manutenção Preditiva" },
  { icon: Server,   label: "Monitorização 24/7" },
  { icon: Globe2,   label: "Starlink Connectivity" },
];

// Partners categories
export const PARTNER_CATEGORIES = [
  "Governos",
  "Fabricantes OEM",
  "Instituições Financeiras",
  "Fundos ESG",
  "Parceiros Tecnológicos",
];

// OEM investment advantages
export const OEM_ADVANTAGES = [
  "Industrialização local",
  "Transferência tecnológica",
  "Redução de custos operacionais",
  "Criação de emprego qualificado",
  "Escalabilidade regional",
];

// Main navigation structure
export const HANNO_NAV = [
  { id: "home",           label: "Home",            to: "/" },
  { id: "hx-energy",      label: "HX Energy",       to: "/hx-energy" },
  { id: "hx-automotive",  label: "HX Automotive",   to: "/hx-automotive" },
  { id: "hx-systems",     label: "HX Systems",      to: "/hx-systems" },
  { id: "hx-infrastructure", label: "HX Infrastructure", to: "/hx-infrastructure" },
  { id: "hx-academy",     label: "HX Academy",      to: "/hx-academy" },
  { id: "projects",       label: "Projects",        to: "/projects" },
  { id: "contact",        label: "Contact",         to: "/contact" },
];

export const HANNO_PEOPLE = [
  { image: "/hanno/people/team-cabo-verde-solar.png",   slug: "cabo-verde", flag: "🇨🇻", location: "Cabo Verde", role_key: "role_solar" },
  { image: "/hanno/people/team-cabo-verde-control.png", slug: "cabo-verde", flag: "🇨🇻", location: "Cabo Verde", role_key: "role_control" },
  { image: "/hanno/people/team-sao-tome-mechanic.png",  slug: "sao-tome",   flag: "🇸🇹", location: "São Tomé e Príncipe", role_key: "role_mechanic" },
  { image: "/hanno/people/team-sao-tome-academy.png",   slug: "sao-tome",   flag: "🇸🇹", location: "São Tomé e Príncipe", role_key: "role_academy" },
  { image: "/hanno/people/team-install-network.png",    slug: "cabo-verde", flag: "🇨🇻", location: "HX Install Network", role_key: "role_network" },
  { image: "/hanno/people/team-engineer-tablet.png",    slug: "cabo-verde", flag: "🇨🇻", location: "Cabo Verde", role_key: "role_engineer" },
];

export const getEcosystemBySlug = (slug) => ECOSYSTEMS.find((e) => e.slug === slug);
